import { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Mail, CreditCard, Save, Camera, Upload, Loader2, MapPin, Phone, Link, Copy, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Use Shopify myshopify.com domain for discount permalink to auto-apply
const SHOPIFY_STORE_DOMAIN = 'kilobeaters-in.myshopify.com';

// Generate proper Shopify discount permalink for auto-apply
const getReferralLink = (referralCode) => {
  if (!referralCode) return 'Loading...';
  return `https://${SHOPIFY_STORE_DOMAIN}/discount/${referralCode}`;
};

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

export default function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [hasProfileDetails, setHasProfileDetails] = useState(false);
  const [profileDetails, setProfileDetails] = useState({
    city: '',
    phone: '',
    address: ''
  });
  const [bankDetails, setBankDetails] = useState({
    bank_name: '',
    account_number: '',
    account_holder: '',
    ifsc_code: ''
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: getAuthHeaders(),
        
      });
      
      if (!response.ok) throw new Error('Failed to fetch profile');
      
      const data = await response.json();
      setUser(data);
      setProfileDetails({
        city: data.city || '',
        phone: data.phone || '',
        address: data.address || ''
      });
      // Check if profile details have been saved before
      setHasProfileDetails(!!(data.city || data.phone || data.address));
      setBankDetails({
        bank_name: data.bank_name || '',
        account_number: data.account_number || '',
        account_holder: data.account_holder || '',
        ifsc_code: data.ifsc_code || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingPicture(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/profile/picture`, {
        method: 'POST',
        headers: getAuthHeaders(),
        
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload picture');
      }

      const data = await response.json();
      setUser(prev => ({ ...prev, picture: data.picture_url }));
      toast.success('Profile picture updated!');
    } catch (error) {
      toast.error(error.message || 'Failed to upload picture');
      console.error(error);
    } finally {
      setUploadingPicture(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSaveProfileDetails = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    
    try {
      const params = new URLSearchParams();
      // Always send all fields, even if empty (to allow clearing values)
      params.append('city', profileDetails.city || '');
      params.append('phone', profileDetails.phone || '');
      params.append('address', profileDetails.address || '');
      
      const response = await fetch(`${API}/profile/details?${params}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        
      });
      
      if (!response.ok) throw new Error('Failed to update profile');
      
      toast.success('Profile details updated!');
      fetchUserProfile();
    } catch (error) {
      toast.error('Failed to update profile');
      console.error(error);
    } finally {
      setSavingProfile(false);
    }
  };

  // IFSC Code validation - RBI format: 4 letters (bank) + 0 + 6 alphanumeric (branch)
  const validateIFSC = (ifsc) => {
    if (!ifsc) return true; // Optional field
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(ifsc.toUpperCase());
  };

  const handleSaveBankDetails = async (e) => {
    e.preventDefault();
    
    // Validate IFSC if provided
    if (bankDetails.ifsc_code && !validateIFSC(bankDetails.ifsc_code)) {
      toast.error('Invalid IFSC Code format. Format: 4 letters + 0 + 6 alphanumeric (e.g., SBIN0001234)');
      return;
    }
    
    try {
      const params = new URLSearchParams(bankDetails);
      const response = await fetch(`${API}/profile/bank-details?${params}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        
      });
      
      if (!response.ok) throw new Error('Failed to update bank details');
      
      toast.success('Bank details updated!');
      fetchUserProfile();
    } catch (error) {
      toast.error('Failed to update bank details');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Profile
        </h1>
        <p className="text-muted-foreground">Manage your account information and settings</p>
      </div>

      {/* User Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="relative group">
              <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="text-3xl bg-primary/10 text-primary">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div 
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadingPicture ? (
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                ) : (
                  <Camera className="h-6 w-6 text-white" />
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePictureUpload}
                disabled={uploadingPicture}
              />
            </div>
            
            <div className="space-y-2 flex-1">
              <div className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5 text-muted-foreground" />
                <span className="font-semibold">{user?.name}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-5 w-5" />
                <span>{user?.email}</span>
              </div>
              {user?.city && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-5 w-5" />
                  <span>{user.city}</span>
                </div>
              )}
              {user?.phone && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-5 w-5" />
                  <span>{user.phone}</span>
                </div>
              )}
              <div className="text-sm text-muted-foreground">
                Member since {new Date(user?.created_at).toLocaleDateString()}
              </div>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPicture}
              >
                {uploadingPicture ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Change Photo
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground mt-1">
                JPG, PNG or GIF. Max 5MB.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location & Contact
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfileDetails} className="space-y-4">
            <div>
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                data-testid="profile-city-input"
                value={profileDetails.city}
                onChange={(e) => setProfileDetails({ ...profileDetails, city: e.target.value })}
                placeholder="e.g., Mumbai, Delhi, Bangalore"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This helps us connect you with local customers
              </p>
            </div>
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                data-testid="profile-phone-input"
                value={profileDetails.phone}
                onChange={(e) => setProfileDetails({ ...profileDetails, phone: e.target.value })}
                placeholder="+91 9876543210"
              />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                data-testid="profile-address-input"
                value={profileDetails.address}
                onChange={(e) => setProfileDetails({ ...profileDetails, address: e.target.value })}
                placeholder="Enter your full address"
              />
            </div>
            <Button data-testid="save-profile-details-button" type="submit" disabled={savingProfile}>
              {savingProfile ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : hasProfileDetails ? (
                <>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Details
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Details
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Bank Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Bank Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveBankDetails} className="space-y-4">
            <div>
              <Label htmlFor="bank_name">Bank Name</Label>
              <Input
                id="bank_name"
                data-testid="profile-bank-name-input"
                value={bankDetails.bank_name}
                onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })}
                placeholder="Enter your bank name"
              />
            </div>
            <div>
              <Label htmlFor="account_number">Account Number</Label>
              <Input
                id="account_number"
                data-testid="profile-account-number-input"
                value={bankDetails.account_number}
                onChange={(e) => setBankDetails({ ...bankDetails, account_number: e.target.value })}
                placeholder="Enter your account number"
              />
            </div>
            <div>
              <Label htmlFor="account_holder">Account Holder Name</Label>
              <Input
                id="account_holder"
                data-testid="profile-account-holder-input"
                value={bankDetails.account_holder}
                onChange={(e) => setBankDetails({ ...bankDetails, account_holder: e.target.value })}
                placeholder="Enter account holder name"
              />
            </div>
            <div>
              <Label htmlFor="ifsc_code">IFSC Code</Label>
              <Input
                id="ifsc_code"
                data-testid="profile-ifsc-code-input"
                value={bankDetails.ifsc_code}
                onChange={(e) => setBankDetails({ ...bankDetails, ifsc_code: e.target.value.toUpperCase() })}
                placeholder="e.g., SBIN0001234"
                maxLength={11}
                pattern="[A-Z]{4}0[A-Z0-9]{6}"
                title="IFSC format: 4 letters + 0 + 6 alphanumeric"
              />
              <p className="text-xs text-muted-foreground mt-1">Format: 4 letters (bank code) + 0 + 6 alphanumeric (branch)</p>
            </div>
            <Button data-testid="save-bank-details-button" type="submit" className="w-full sm:w-auto">
              <Save className="h-4 w-4 mr-2" />
              Save Bank Details
            </Button>
          </form>
          
          {user?.bank_name && (
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Current Bank Details:</p>
              <div className="text-sm text-muted-foreground space-y-1">
                <p><span className="font-medium">Bank:</span> {user.bank_name}</p>
                <p><span className="font-medium">Account:</span> ****{user.account_number?.slice(-4)}</p>
                <p><span className="font-medium">Holder:</span> {user.account_holder}</p>
                {user.ifsc_code && <p><span className="font-medium">IFSC:</span> {user.ifsc_code}</p>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Referral Code Card */}
      <Card>
        <CardHeader>
          <CardTitle>Your Referral Code & Link</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Referral Code */}
            <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Your Referral Code</p>
                <p className="font-mono font-bold text-2xl text-primary">{user?.referral_code || 'Loading...'}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(user?.referral_code || '');
                  toast.success('Referral code copied!');
                }}
                data-testid="copy-referral-code-button"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Code
              </Button>
            </div>

            {/* Referral Link */}
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex-1 mr-4">
                <p className="text-sm text-muted-foreground mb-1">Your Referral Link (Auto-applies 10% discount)</p>
                <p className="font-mono text-sm text-blue-600 break-all">
                  {getReferralLink(user?.referral_code)}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-blue-300 text-blue-600 hover:bg-blue-100"
                onClick={() => {
                  navigator.clipboard.writeText(getReferralLink(user?.referral_code));
                  toast.success('Referral link copied!');
                }}
                data-testid="copy-referral-link-button"
              >
                <Link className="h-4 w-4 mr-2" />
                Copy Link
              </Button>
            </div>
            
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">How to use:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Share your referral link directly with customers</li>
                <li>• Or ask customers to mention your code when purchasing</li>
                <li>• Code should be added to Shopify orders for tracking</li>
                <li>• All revenue with this code will be credited to you</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Partner ID Card */}
      <Card>
        <CardHeader>
          <CardTitle>Partner ID</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Your unique partner ID</p>
              <p className="font-mono font-semibold">{user?.user_id}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(user?.user_id);
                toast.success('Partner ID copied!');
              }}
              data-testid="copy-partner-id-button"
            >
              Copy
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
